import { Container, GridProps, Typography } from "@mui/material";
import {
  SmartCheckbox,
  SmartForm,
  SmartTextField,
} from "@nodeon/react-components";
import { millisecondsToHours } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  AtonUnitDetails,
  InSeaAtonLocation,
  InSeaFullAtonDetails,
  useSeaDaticsApi,
} from "~/api/GeneratedApi";
import LoadingBox from "~/components/LoadingBox";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";

type FormData = {
  buoyId: string;
  name: string;
  atoNNumber: string;
  originalPosition: string;
  floating: boolean;
  buoyType: string;
  nightToDay: string;
  dayToNight: string;
  hdop: string;
  lightDistance: string;
  illuminationCounter: string;
  opticalFeedbackLevel: string;
  tiltAngle: string;
  solarPanel24hProduction: string;
  solarPanelCurrentMax: string;
  secondaryBatteryVoltage: string;
  beaconTemperature: string;
  voltage: string;
  calculatedDeviation: string;
  photocellThreshold: string;
  minimumVoltage: string;
  flashTable: string;
  powerConsumption: string;
  installationPosition: string;
  maxDeviation: string;
  defaultLedIntensity: string;
  avg24Voltage: string;
  min24Voltage: string;
  currentPosition: string;
  communicationStatus: string;
  commDeviceType: string;
  smcFirmware: string;
  gpsFirmware: string;
  gpsHardware: string;
  gsmFirmware: string;
  gsmSoftware: string;
  lastConnected: string;
  activeAlarms: string;
  activeWarnings: string;
};

interface Props {
  atonData: AtonUnitDetails;
  small?: boolean;
}

export const AtoNRemoteMonitoringTab = ({
  atonData,
  small,
}: Props): JSX.Element => {
  const { t } = useTranslation("aton-remote-monitoring");
  const disabled = true;
  const { getAtonUnit } = useSeaDaticsApi();
  const { dataToForm } = useDataToForm();

  // Use actual number, unless on dev and aton does not have remote monitoring
  // In that case, use a static number
  const seaDaticsAtonNumber = React.useMemo(
    () =>
      !atonData.atonUnit.seaDaticsRemoteMonitoring &&
      process.env.NODE_ENV === "development"
        ? 8519
        : atonData.atonUnit.atonNumber,
    [atonData],
  );

  const { isLoading, data: buoyData } = useCustomQuery(
    { key: "aton-remote-monitoring", id: seaDaticsAtonNumber },
    () => getAtonUnit(seaDaticsAtonNumber),
  );

  const fieldSize: GridProps = React.useMemo(
    () => (small ? { xs: 4 } : { xs: 4, lg: 3 }),
    [small],
  );

  if (isLoading) return <LoadingBox />;

  if (!buoyData)
    return (
      <Container>
        <Typography textAlign="center">
          {t("no-remote-monitoring-data")}
        </Typography>
      </Container>
    );

  return (
    <Container>
      <SmartForm<FormData, null, true>
        onSubmit={() => null}
        formProps={{
          defaultValues: dataToForm(buoyData),
        }}
      >
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="name"
          label={t("name")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="voltage"
          label={t("voltage")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "V" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="avg24Voltage"
          label={t("avg-24-voltage")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "V" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="min24Voltage"
          label={t("min-24-voltage")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "V" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="communicationStatus"
          label={t("communication-status")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="originalPosition"
          label={t("original-position")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="activeAlarms"
          label={t("active-alarms")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="activeWarnings"
          label={t("active-warnings")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="calculatedDeviation"
          label={t("calculated-deviation")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "m" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="maxDeviation"
          label={t("max-deviation")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "m" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="beaconTemperature"
          label={t("beacon-temperature")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "°C" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="powerConsumption"
          label={t("power-consumption")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "W" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="currentPosition"
          label={t("current-position")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="nightToDay"
          label={t("night-to-day")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="dayToNight"
          label={t("day-to-night")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="hdop"
          label={t("hdop")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="lastConnected"
          label={t("last-connected")}
          gridProps={fieldSize}
        />
        <SmartCheckbox<FormData>
          name="floating"
          label={t("floating")}
          disabled={disabled}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="buoyType"
          label={t("buoy-type")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="commDeviceType"
          label={t("comm-device-type")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="photocellThreshold"
          label={t("photocell-threshold")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "lx" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="minimumVoltage"
          label={t("minumum-voltage")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "V" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="flashTable"
          label={t("flash-table")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="smcFirmware"
          label={t("smc-firmware")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="gpsHardware"
          label={t("gps-hardware")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="gpsFirmware"
          label={t("gps-firmware")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="gsmFirmware"
          label={t("gsm-firmware")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="gsmSoftware"
          label={t("gsm-software")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="installationPosition"
          label={t("installation-position")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="defaultLedIntensity"
          label={t("default-led-intensity")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "%" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="lightDistance"
          label={t("light-distance")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "NM" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="illuminationCounter"
          label={t("illumination-counter")}
          gridProps={fieldSize}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="opticalFeedbackLevel"
          label={t("optical-feedback-level")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "%" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="tiltAngle"
          label={t("tilt-angle")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "°" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="solarPanel24hProduction"
          label={t("solar-panel-24h-production")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "Ah" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="solarPanelCurrentMax"
          label={t("solar-panel-current-max")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "A" }}
        />
        <SmartTextField<FormData>
          fullWidth
          disabled={disabled}
          name="secondaryBatteryVoltage"
          label={t("secondary-battery-voltage")}
          gridProps={fieldSize}
          InputProps={{ endAdornment: "V" }}
        />
      </SmartForm>
    </Container>
  );
};

type UseDataToFormReturn = {
  dataToForm: (data: InSeaFullAtonDetails) => FormData;
};
function useDataToForm(): UseDataToFormReturn {
  const dateFormatter = useDateFormatter();
  const dataToForm = React.useCallback(
    (d: InSeaFullAtonDetails): FormData => {
      return {
        buoyId: d.buoyId?.toString() ?? "",
        name: d.name ?? "",
        atoNNumber: d.atoNNumber?.toString() ?? "",
        originalPosition: locationToString(d.originalPosition),
        floating: d.floating ?? false,
        buoyType: d.buoyType ?? "",
        nightToDay: d.nightToDay ? dateFormatter({ date: d.nightToDay }) : "",
        dayToNight: d.dayToNight ? dateFormatter({ date: d.dayToNight }) : "",
        hdop: d.hdop?.toString() ?? "",
        lightDistance: d.lightDistance?.toString() ?? "",
        illuminationCounter: formatIlluminationCounter(d.illuminationCounter),
        opticalFeedbackLevel: d.opticalFeedbackLevel?.toString() ?? "",
        tiltAngle: d.tiltAngle?.toString() ?? "",
        solarPanel24hProduction: d.solarPanel24hProduction?.toString() ?? "",
        solarPanelCurrentMax: d.solarPanelCurrentMax?.toString() ?? "",
        secondaryBatteryVoltage: d.secondaryBatteryVoltage?.toString() ?? "",
        beaconTemperature: d.beaconTemperature?.toString() ?? "",
        voltage: d.voltage?.toString() ?? "",
        calculatedDeviation: d.calculatedDeviation?.toString() ?? "",
        photocellThreshold: d.photocellThreshold?.toString() ?? "",
        minimumVoltage: d.minimumVoltage?.toString() ?? "",
        flashTable: d.flashTable?.toString() ?? "",
        powerConsumption: d.powerConsumption?.toString() ?? "",
        installationPosition: locationToString(d.installationPosition),
        maxDeviation: d.maxDeviation?.toString() ?? "",
        defaultLedIntensity: d.defaultLedIntensity?.toString() ?? "",
        avg24Voltage: d.avg24Voltage?.toString() ?? "",
        min24Voltage: d.min24Voltage?.toString() ?? "",
        currentPosition: locationToString(d.currentPosition),
        communicationStatus: d.communicationStatus ?? "",
        commDeviceType: d.commDeviceType?.toString() ?? "",
        smcFirmware: d.smcFirmware ?? "",
        gpsFirmware: d.gpsFirmware ?? "",
        gpsHardware: d.gpsHardware ?? "",
        gsmFirmware: d.gsmFirmware ?? "",
        gsmSoftware: d.gsmSoftware ?? "",
        lastConnected: d.lastConnected
          ? dateFormatter({ date: new Date(d.lastConnected) })
          : "",
        activeAlarms: d.activeAlarms?.toString() ?? "",
        activeWarnings: d.activeWarnings?.toString() ?? "",
      };
    },
    [dateFormatter],
  );
  return { dataToForm };
}

function locationToString(l: InSeaAtonLocation | undefined): string {
  return l && l.latitude && l.longitude ? `${l.longitude}, ${l.latitude}` : "";
}

function formatIlluminationCounter(i: number | undefined): string {
  if (i === undefined || i === null) return "";
  const hours = millisecondsToHours(i * 10);
  return hoursToDaysAndHours(hours);
}

function hoursToDaysAndHours(totalHours: number): string {
  const days = Math.floor(totalHours / 24);
  const remainder = totalHours % 24;
  const hours = Math.floor(remainder);
  return `${days}d ${hours}h`;
}
